import { useCallback, useEffect, useState } from 'react';
import type { ILocalAudioTrack } from 'agora-rtc-sdk-ng';
import useVideoContext from '../useVideoContext/useVideoContext';

/** Agora: `setMuted(true)` stops sending; UI must follow `.muted`, not `.enabled` (often false while mic is still “live”). */
function isMicSending(track: ILocalAudioTrack): boolean {
  return track.muted !== true;
}

export default function useLocalAudioToggle() {
  const { localTracks } = useVideoContext();
  const audioTrack = localTracks.find((t): t is ILocalAudioTrack => t.trackMediaType === 'audio');
  const [, bump] = useState(0);

  useEffect(() => {
    if (!audioTrack) return;
    const onUpdated = () => bump(n => n + 1);
    onUpdated();
    audioTrack.on('track-updated', onUpdated);
    const mst = audioTrack.getMediaStreamTrack?.();
    if (mst) {
      mst.addEventListener('mute', onUpdated);
      mst.addEventListener('unmute', onUpdated);
    }
    return () => {
      audioTrack.off('track-updated', onUpdated);
      if (mst) {
        mst.removeEventListener('mute', onUpdated);
        mst.removeEventListener('unmute', onUpdated);
      }
    };
  }, [audioTrack]);

  const toggleAudioEnabled = useCallback(async () => {
    if (!audioTrack || typeof audioTrack.setMuted !== 'function') return;
    const sending = isMicSending(audioTrack);
    try {
      await audioTrack.setMuted(sending);
    } catch {
      /* track-updated / MST events will sync */
    }
    bump(n => n + 1);
    queueMicrotask(() => bump(n => n + 1));
  }, [audioTrack]);

  const isAudioEnabled = audioTrack ? isMicSending(audioTrack) : false;

  return [isAudioEnabled, toggleAudioEnabled] as const;
}
